import { holdsEveryJob, type JobId } from './jobs.js';

export const MEMBER_ROLES = ['assistant', 'member'] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

export const AUTONOMY_MODES = ['off', 'supervised', 'auto'] as const;
/**
 * off        - never runs a task without an explicit start from the user
 * supervised - runs assigned tasks, but every risky action needs approval
 * auto       - runs assigned tasks and handles routine risk itself
 */
export type AutonomyMode = (typeof AUTONOMY_MODES)[number];

export const EFFORT_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;
/**
 * How hard the model thinks per call, for providers that take an effort knob
 * (Anthropic API, Claude CLI). Null on a member means the provider/model default;
 * other providers ignore it.
 */
export type EffortLevel = (typeof EFFORT_LEVELS)[number];

/** @deprecated Limits are now stored per API key and enforced by the server registry. */
export interface RateLimits {
  readonly rpm: number | null;
  readonly inputTokensPerMinute: number | null;
  readonly outputTokensPerMinute: number | null;
}
/** @deprecated Compatibility constant for older integrations. */
export const NO_RATE_LIMITS: RateLimits = {
  rpm: null,
  inputTokensPerMinute: null,
  outputTokensPerMinute: null,
};

/**
 * Per-minute caps on one stored API key, set in the Providers tab.
 *
 * Null means unlimited. These are enforced server-side by the shared key
 * registry, so every member that points at the same key sees the same windows
 * and the same in-flight count. They do not replace the provider's own quota;
 * they keep one key's traffic inside what the user knows it can take.
 */
export interface KeyRateLimits {
  /** Model calls (stream requests) allowed per minute. */
  readonly rpm: number | null;
  /** Input tokens allowed per minute. */
  readonly inputTokensPerMinute: number | null;
  /** Output tokens allowed per minute. */
  readonly outputTokensPerMinute: number | null;
  /**
   * How many calls may run on this key at the same time.
   *
   * A provider key is a single credential: two concurrent calls on one key can
   * trip a provider-side concurrency limit that no per-minute window would
   * catch. Null means no cap.
   */
  readonly maxConcurrentCalls: number | null;
}

export const NO_KEY_LIMITS: KeyRateLimits = {
  rpm: null,
  inputTokensPerMinute: null,
  outputTokensPerMinute: null,
  maxConcurrentCalls: null,
};

/**
 * One provider/key/model triple a member dials with.
 *
 * A member holds an ordered list of these rather than a single binding, so a
 * team can mix providers, mix keys within a provider, or point different
 * members at different models on the same provider. The list is walked one by
 * one; the worker only switches when it hits a rate limit.
 */
export interface MemberBinding {
  /** Builtin provider id. */
  readonly provider: string;
  /**
   * Which stored key this binding dials with.
   *
   * Null means the provider's default, which is its most recently added key.
   */
  readonly keyId: number | null;
  readonly model: string;
}

export interface Member {
  readonly id: number;
  readonly projectId: number;
  readonly name: string;
  readonly role: MemberRole;
  /** Explicit job assignment, including the Assistant. */
  readonly jobs: readonly JobId[];
  /**
   * The ordered list of provider/key/model bindings this member dials with.
   *
   * Empty means the member has no model configured yet and cannot run. The
   * first entry is preferred; the rest are rotation targets on rate limits.
   */
  readonly bindings: readonly MemberBinding[];
  /** Deprecated single-binding mirrors for older API clients. */
  readonly provider: string | null;
  readonly providerKeyId: number | null;
  readonly providerBackupKeyId: number | null;
  readonly model: string | null;
  readonly rateLimitRpm: number | null;
  readonly rateLimitInputTpm: number | null;
  readonly rateLimitOutputTpm: number | null;
  readonly enabled: boolean;
  readonly autonomy: AutonomyMode;
  /** Explicit opt-in to run this member's shell commands without approval prompts. */
  readonly autoAllowShell: boolean;
  /** Explicit opt-in to apply file creates and exact edits without approval prompts. */
  readonly autoAllowEdits: boolean;
  /** Auto-Approve: every approval request (shell, edits, deletes, moves, external tools) is approved at once. */
  readonly autoApproveAll: boolean;
  /**
   * Keep working after assigned prompts and board tasks are done: file new tasks
   * and assign them. Effective only when this member holds every job.
   */
  readonly autonomousWork: boolean;
  /**
   * "Reverse Engineer and Duplicate Game": the member always gets the REA and
   * Desktop control MCP servers and the study -> build -> compare workflow prompt.
   */
  readonly reverseEngineerGame: boolean;
  /**
   * Effort for every model call this member makes. Null leaves it to the
   * provider/model default. Changes reach a running worker without a restart.
   */
  readonly effort: EffortLevel | null;
  /**
   * Ultracode: the member may fan work out to parallel sub-agents (the
   * spawn_agents tool on the API loop, Claude Code's own workflows on the CLI).
   * Toggling it restarts the worker.
   */
  readonly ultracode: boolean;
  readonly createdAt: string;
}

/** The assistant is the only required member and the only one you talk to. */
export const ASSISTANT_ROLE: MemberRole = 'assistant';

export function isMemberRole(value: unknown): value is MemberRole {
  return typeof value === 'string' && (MEMBER_ROLES as readonly string[]).includes(value);
}

export function isAutonomyMode(value: unknown): value is AutonomyMode {
  return typeof value === 'string' && (AUTONOMY_MODES as readonly string[]).includes(value);
}

export function isEffortLevel(value: unknown): value is EffortLevel {
  return typeof value === 'string' && (EFFORT_LEVELS as readonly string[]).includes(value);
}

/**
 * Autonomous work runs only when the checkbox is on and every job is held.
 * Dropping a job turns the behavior off without clearing the stored checkbox.
 */
export function autonomousWorkActive(member: {
  readonly autonomousWork?: boolean;
  readonly jobs: readonly string[];
}): boolean {
  return member.autonomousWork === true && holdsEveryJob(member.jobs);
}

export interface CreateMemberInput {
  readonly projectId: number;
  readonly name: string;
  readonly role?: MemberRole;
  readonly jobs?: readonly JobId[];
  readonly bindings?: readonly MemberBinding[];
  /** Legacy single-binding fields accepted while older clients migrate. */
  readonly provider?: string | null;
  readonly providerKeyId?: number | null;
  readonly model?: string | null;
  readonly providerBackupKeyId?: number | null;
  readonly rateLimitRpm?: number | null;
  readonly rateLimitInputTpm?: number | null;
  readonly rateLimitOutputTpm?: number | null;
  readonly enabled?: boolean;
  readonly autonomy?: AutonomyMode;
  readonly autoAllowShell?: boolean;
  readonly autoAllowEdits?: boolean;
  /** Approve every approval request at once. Off when omitted. */
  readonly autoApproveAll?: boolean;
  /** When omitted, the member still stops between requests. */
  readonly autonomousWork?: boolean;
  /** "Reverse Engineer and Duplicate Game" workflow. Off when omitted. */
  readonly reverseEngineerGame?: boolean;
  /** Null or omitted means the provider/model default. */
  readonly effort?: EffortLevel | null;
  /** Parallel sub-agents. Off when omitted. */
  readonly ultracode?: boolean;
}

export const MEMBER_NAME_MAX = 40;

/** Members need stable, typeable names because the Assistant addresses them by name. */
export function assertMemberName(name: string): string {
  const trimmed = name.trim().replace(/\s+/g, ' ');
  if (!trimmed) throw new Error('Member name is required.');
  if (trimmed.length > MEMBER_NAME_MAX) {
    throw new Error(`Member name must be ${MEMBER_NAME_MAX} characters or fewer.`);
  }
  if (!/^[\p{L}\p{N}][\p{L}\p{N} ._-]*$/u.test(trimmed)) {
    throw new Error(
      'Member name may contain letters, digits, spaces, dots, dashes and underscores.',
    );
  }
  return trimmed;
}

/** A member with no jobs can never do anything; callers should reject that. */
export function assertHasJobs(jobs: readonly JobId[], role: MemberRole): void {
  if (role === 'assistant') return;
  if (jobs.length === 0) {
    throw new Error('Assign at least one job, or this member will be unable to act.');
  }
}

/** @deprecated New workers use ordered bindings and the shared registry. */
export function rateLimitsFor(member: {
  rateLimitRpm: number | null;
  rateLimitInputTpm: number | null;
  rateLimitOutputTpm: number | null;
}): RateLimits {
  return {
    rpm: member.rateLimitRpm,
    inputTokensPerMinute: member.rateLimitInputTpm,
    outputTokensPerMinute: member.rateLimitOutputTpm,
  };
}

/** @deprecated New workers use MemberBinding[]. */
export function apiKeySlots(member: {
  providerKeyId: number | null;
  providerBackupKeyId: number | null;
}): (number | null)[] {
  return member.providerBackupKeyId === null || member.providerBackupKeyId === member.providerKeyId
    ? [member.providerKeyId]
    : [member.providerKeyId, member.providerBackupKeyId];
}

/**
 * Autonomous instructions for an Assistant working alone (no other enabled
 * member): it does every task itself instead of filing work for a team, and
 * never runs out of work.
 */
export const SOLO_AUTONOMOUS_WORK = `Autonomous work is on and you are the only member, so do the work yourself. Never delegate, and never stop to ask whether to continue. Work in CYCLES; one cycle is one slice the user could try.
1. ORIENT (briefly): read_working_memory and memory_read for the plan and the cycle log, read the design docs (AGENTS.md, docs/), read_board for tasks already assigned to you, and read_console for errors. Finish what is already in front of you first: an unfinished prompt, a ready board task (move it to in_progress with update_task while you work and to review when it can be tried; the user approves review tasks, never mark one done yourself), a generation waiting for your decision.
2. CHOOSE: when nothing is waiting, pick the single most valuable next improvement. Weigh visible impact for a player, what unblocks other work, and effort; rotate between areas (gameplay and systems, models, pixel art, sound and music, levels and scenes, UI, bugs, performance, polish) instead of staying in one, and do not repeat what the cycle log shows is done. State the goal and a short checklist of what "done" means in working memory (update_working_memory).
3. BUILD in small vertical steps, simplest working version first. Make assets with the right tool: procedural models and rigs, sewn clothes and fabric materials, prism_image_generate for textures, prints, icons and concept art, and sound documents for audio. Wire every asset into the game (scenes, scripts, materials, sounds); an asset nobody uses is not progress.
4. VERIFY like a critic: prism_check, prism_screenshot or prism_model_render, prism_run_game, read_console. For gameplay, read what actually happened instead of guessing from screenshots: read_game_trace (collisions, triggers, inputs, script logs and errors, state changes; poll with since) and game_state (live entities, positions, components) while Play runs, or prism_play_headless with an input script for a repeatable test. Compare against the checklist, fix every defect you find, and look again. When a generation result is waiting for YOUR decision, judge it strictly against the request (render or listen to it) and accept, deny or redo it with a better prompt, generator or chain; redo a clearly off result up to two times, then change approach. If a switch makes approval automatic, you still run every refine and check; do not skip quality because nothing can reject the result.
VIDEO: for a cutscene or film (*.video.json, Video Studio) be the director and writer: prism_video_create, then prism_video_storyboard (bible, characters with a look and a voice, locations, acts, scenes, shots with camera, dialogue, sound cues, music with lyrics), then prism_video_render in order: preset "animatic" (character sheets, keyframes, fast animatic clips) and review every clip against the bible (faces, wardrobe, location, story beat, continuity with the previous shot); then "final" (final shots chained from the previous last frame and re-anchored to keyframes, engine shots via prism_video_sequence + prism record, voice-overs, lip sync, post), then "audio" (sfx, music) and prism_video_assemble. Watch the frames each review returns; redo or refine any shot that breaks continuity. prism_video_status shows what is missing.
5. RECORD: save the cycle in memory_write (what changed, why, outcome, what to do next, anything that failed), save_checkpoint when a slice works, update the board task if one exists, then call finish with what changed and how to try it. Immediately start the next cycle.
STUCK RULE: if the same obstacle beats you twice, stop repeating it: shrink the scope, switch approach, or log it and move to another area. Never loop on a failing step. The work never runs out: there is always something to build, fix, or make better.`;
